import { FormError } from "@3gaam/shared/types/error";
import React, { useCallback } from "react";
import ReactSelect, {
  components,
  GroupBase,
  Props as ReactSelectProps,
} from "react-select";
import Icon from "../icon";

type StylePops = {
  width?: number;
  iconContent?: boolean;
  noBorder?: boolean;
  showError?: boolean;
};

type CustomProps = {
  // @deprecated
  optionLabel?: string;
  optionValue?: string;
  error?: FormError;
  icon?: any;
  height?: number;
} & StylePops;

type Props<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Partial<
  Omit<ReactSelectProps<Option, IsMulti, Group>, "onChange"> & CustomProps
> & {
  onChange?: (e: any, param?: any) => void;
};

export type DefaultSelectFieldProps = Props<unknown, false, GroupBase<unknown>>;

// FIXME: fix type
const findOptionByValue = (options: any, value: any) => {
  if (!options) return;
  return options?.find((o: any) => `${o?.value}` === `${value}`);
};

const NoOptionsMessage = (props: any) => {
  // FIXME: fix type
  return (
    <components.NoOptionsMessage {...props}>
      <span>بدون گزینه انتخابی</span>
    </components.NoOptionsMessage>
  );
};

type OptionBase = {
  value: any;
  label: string;
};

/** extending react-selects */
function SelectField<
  Option = OptionBase,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  width,
  iconContent,
  noBorder,
  onChange,
  error,
  icon,
  value,
  height,
  ...props
}: Props<Option, IsMulti, Group>) {
  const option2Event = useCallback((o: any, name?: string) => {
    return { target: { ...o, name } };
  }, []);

  let showError =
    props.name && error?.hasError && error?.dataError?.[props.name];

  return (
    <div className={`inp-box select  ${showError ? "invalid-field" : ""}`}>
      {iconContent ? (
        iconContent
      ) : (
        <span className="inp-icon">
          <Icon name={icon} />
        </span>
      )}
      <ReactSelect
        {...props}
        value={
          typeof value === "object"
            ? value
            : findOptionByValue(props.options, value)
        }
        styles={{
          option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isFocused ? "#fbf2ff" : undefined,
            color: isSelected ? "#6e00aa" : undefined,
            fontWeight: isSelected ? "500" : undefined,
            textAlign: "right",
            borderRadius: 6,
            textTransform: "capitalize",
            cursor: "pointer",
          }),
          container: (base) => ({ ...base, borderRadius: "50%" }),
          control: (base, { isFocused }) => ({
            ...base,
            minWidth: width || 140,
            height,
            boxShadow: "none",
            borderRadius: 6,
            textTransform: "capitalize",
            cursor: "pointer",
            ...(iconContent ? { paddingRight: "40px" } : {}),
            ...(showError ? { border: "1px solid #ea1e63" } : {}),
          }),
          valueContainer: (base) => ({ ...base }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: "0 3px 0",
          }),
          menu: (base) => ({
            ...base,
            zIndex: 2,
          }),
          menuList: (base) => ({
            ...base,
            padding: 8,
          }),
        }}
        theme={(theme) => ({
          ...theme,
          borderRadius: 8,
          colors: {
            ...theme.colors,
            primary75: "#af1aff",
            primary: "#6545a4",
            primary25: "#e7daff",
            primary50: "#ceb5ff",
          },
        })}
        placeholder={props.placeholder || false}
        components={{
          IndicatorSeparator: null,
          NoOptionsMessage,
        }}
        onChange={(event) => {
          if (!onChange) return;
          onChange(option2Event(event, props.name));
        }}
      />
      {showError && props.name && (
        <p className="text-red font-10 mt-1" data-testid="selector__error">
          {error?.dataError?.[props.name]}
        </p>
      )}
    </div>
  );
}

export default React.memo(SelectField);
